import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Archive, ArchiveRestore, Copy, Ellipsis, Pencil, Plus, Ticket, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { plansQuery, useDeletePlan, useRetirePlan, useUpdatePlan, type PassPlan } from '@/api/passes'
import { branchesQuery } from '@/api/queries'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { AppTag } from '@/app/passes/app-tag'
import { PassesTabs } from '@/app/passes/passes-tabs'
import { errorText } from '@/domain/errors'
import { canManagePlans, canSeePasses, canSeeSold } from '@/domain/pass-abilities'
import { summarize } from '@/domain/pass-describe'
import { formatMoney } from '@/domain/pass-rules'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem } from '@/ui/overlays'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/passes/plans/')({
  component: PriceList,
})

type News = Readonly<{ kind: 'retired' | 'restored' | 'deleted'; name: string }>

const p = () => t.passes.plans

/// «Тарифи»: the price list — the section's second page, after the sold
/// passes. Seen from `passes ≥ view`; created, changed,
/// duplicated and retired by `passes = manage`. Every row says in one line
/// what the plan IS, generated from its blocks.
function PriceList() {
  const { orgId } = Route.useParams()
  const { caller } = useCompanyCaller(orgId)
  const navigate = useNavigate()
  const allowed = canSeePasses(caller)
  const manage = canManagePlans(caller)
  const plans = useQuery({ ...plansQuery(orgId), enabled: allowed })
  const branches = useQuery({ ...branchesQuery(orgId), enabled: allowed })
  const [retiring, setRetiring] = useState<PassPlan | null>(null)
  const [deleting, setDeleting] = useState<PassPlan | null>(null)
  const [news, setNews] = useState<News | null>(null)
  const restore = useUpdatePlan(orgId)

  if (!allowed) return <NothingForRole />

  const list = plans.data ?? []
  const onSale = list.filter((plan) => plan.active)
  const retired = list.filter((plan) => !plan.active)
  const branchName = (id: string | null) =>
    id === null ? p().allBranches : ((branches.data ?? []).find((x) => x.id === id)?.name ?? '')

  const open = (plan: PassPlan) =>
    void navigate({ to: '/c/$orgId/passes/plans/$planId', params: { orgId, planId: plan.id } })
  const addButton = manage ? (
    <Button
      variant="primary"
      icon={<Plus className="size-[18px]" strokeWidth={2.5} />}
      onClick={() => void navigate({ to: '/c/$orgId/passes/plans/new', params: { orgId } })}
    >
      {p().add}
    </Button>
  ) : undefined

  const restorePlan = (plan: PassPlan) =>
    restore.mutate(
      {
        planId: plan.id,
        input: {
          name: plan.name,
          description: plan.description,
          priceMinor: plan.priceMinor,
          currency: plan.currency,
          branchId: plan.branchId,
          rules: plan.rules,
          active: true,
        },
      },
      { onSuccess: () => setNews({ kind: 'restored', name: plan.name }) },
    )

  /// Name · what it is · where it sells · price — the price list read the
  /// way a director reads it, the price right-aligned in tabular figures.
  /// The rules summary is the plan's own words, generated from its blocks.
  const columns: readonly DataColumn<PassPlan>[] = [
    {
      key: 'name',
      header: p().columns.name,
      width: 'minmax(0,1.1fr)',
      // On sale in the app (specs/online-sale.md §2): a quiet tag after the
      // name — the one fact the list cannot tell from the price.
      cell: (plan) =>
        plan.soldOnline && plan.active ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{plan.name}</span>
            <AppTag iconOnly />
          </span>
        ) : (
          plan.name
        ),
    },
    {
      key: 'rules',
      header: p().columns.rules,
      width: 'minmax(0,2fr)',
      hideBelow: 'md',
      cell: (plan) => {
        const words = summarize(plan.rules)
        return words ? <span title={words}>{words}</span> : <span className="text-muted">—</span>
      },
    },
    {
      key: 'branch',
      header: p().columns.branch,
      width: 'minmax(0,0.8fr)',
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (plan) => branchName(plan.branchId),
    },
    {
      key: 'price',
      header: p().columns.price,
      width: '112px',
      numeric: true,
      mobile: 'trailing',
      // The figure people scan for: callout-sized, in ink unless retired.
      cell: (plan) => (
        <span className={cn('text-callout', plan.active && 'font-medium text-text')}>
          {formatMoney(plan.priceMinor, plan.currency)}
        </span>
      ),
    },
  ]

  const menu = (plan: PassPlan) =>
    manage ? (
      <RowMenu label={`${t.app.more}: ${plan.name}`} trigger={<Ellipsis className="size-[18px]" />}>
        <RowMenuItem icon={<Pencil className="size-4" />} onSelect={() => open(plan)}>
          {p().edit}
        </RowMenuItem>
        <RowMenuItem
          icon={<Copy className="size-4" />}
          onSelect={() => void navigate({ to: '/c/$orgId/passes/plans/new', params: { orgId }, search: { from: plan.id } })}
        >
          {p().duplicate}
        </RowMenuItem>
        {plan.active ? (
          <RowMenuItem destructive icon={<Archive className="size-4" />} onSelect={() => setRetiring(plan)}>
            {p().retire}
          </RowMenuItem>
        ) : (
          <RowMenuItem icon={<ArchiveRestore className="size-4" />} onSelect={() => restorePlan(plan)}>
            {p().restore}
          </RowMenuItem>
        )}
        <RowMenuItem destructive icon={<Trash2 className="size-4" />} onSelect={() => setDeleting(plan)}>
          {p().delete}
        </RowMenuItem>
      </RowMenu>
    ) : null

  const table = (rows: readonly PassPlan[], label: string) => (
    <DataList
      label={label}
      columns={columns}
      rows={rows}
      rowKey={(plan) => plan.id}
      rowTarget={(plan) => ({ to: '/c/$orgId/passes/plans/$planId', params: { orgId, planId: plan.id } })}
      rowLabel={(plan) => `${manage ? p().edit : p().open}: ${plan.name}`}
      rowActions={manage ? menu : undefined}
      rowTone={(plan) => (plan.active ? 'normal' : 'muted')}
      empty={p().empty}
    />
  )

  return (
    <Page wide>
      <PageHeader
        title={t.passes.title}
        subtitle={t.passes.subtitle}
        actions={list.length > 0 ? addButton : undefined}
        tabs={<PassesTabs orgId={orgId} showSold={canSeeSold(caller)} />}
      />

      {news ? (
        <InlineBanner
          kind="done"
          className="mb-6"
          message={
            news.kind === 'retired'
              ? p().retiredBanner(news.name)
              : news.kind === 'deleted'
                ? p().deletedBanner(news.name)
                : p().restoredBanner(news.name)
          }
        />
      ) : null}
      {restore.isError ? <InlineBanner kind="error" className="mb-6" message={errorText(restore.error)} /> : null}

      {plans.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : plans.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(plans.error)}
          action={{ label: t.app.retry, onClick: () => void plans.refetch() }}
        />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<Ticket />}
          title={p().empty}
          message={manage ? p().emptyMessage : p().emptyReadOnly}
          action={addButton}
        />
      ) : (
        <div className="flex flex-col gap-10">
          {manage ? null : <InlineBanner kind="info" message={p().readOnly} />}
          {table(onSale, p().onSale)}
          {retired.length > 0 ? (
            <section aria-labelledby="retired" className="flex flex-col gap-2">
              <SectionHeader id="retired" title={p().retired} count={retired.length} />
              {table(retired, p().retired)}
              <p className="px-4 text-caption text-pretty text-muted">{p().retiredFootnote}</p>
            </section>
          ) : null}
        </div>
      )}

      {manage ? (
        <RetireDialog
          orgId={orgId}
          plan={retiring}
          onClose={() => setRetiring(null)}
          onDone={(plan) => {
            setRetiring(null)
            setNews({ kind: 'retired', name: plan.name })
          }}
        />
      ) : null}
      {manage ? (
        <DeleteDialog
          orgId={orgId}
          plan={deleting}
          onClose={() => setDeleting(null)}
          onDone={(plan) => {
            setDeleting(null)
            setNews({ kind: 'deleted', name: plan.name })
          }}
        />
      ) : null}
    </Page>
  )
}

function RetireDialog({
  orgId,
  plan,
  onClose,
  onDone,
}: {
  orgId: string
  plan: PassPlan | null
  onClose: () => void
  onDone: (plan: PassPlan) => void
}) {
  const retire = useRetirePlan(orgId)
  return (
    <ConfirmDialog
      open={Boolean(plan)}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          retire.reset()
        }
      }}
      title={plan ? p().retireTitle(plan.name) : ''}
      message={p().retireMessage}
      confirmLabel={p().retireConfirm}
      busy={retire.isPending}
      error={retire.isError ? <InlineBanner kind="error" message={errorText(retire.error)} /> : null}
      onConfirm={() => {
        if (plan) retire.mutate(plan.id, { onSuccess: () => onDone(plan) })
      }}
    />
  )
}

/// Deleting is for mistakes — a test plan, a typo. The dialog says what stays
/// (every pass already sold) and points at the gentler door, retiring.
function DeleteDialog({
  orgId,
  plan,
  onClose,
  onDone,
}: {
  orgId: string
  plan: PassPlan | null
  onClose: () => void
  onDone: (plan: PassPlan) => void
}) {
  const remove = useDeletePlan(orgId)
  return (
    <ConfirmDialog
      open={Boolean(plan)}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          remove.reset()
        }
      }}
      title={plan ? p().deleteTitle(plan.name) : ''}
      message={p().deleteMessage}
      confirmLabel={p().deleteConfirm}
      busy={remove.isPending}
      error={remove.isError ? <InlineBanner kind="error" message={errorText(remove.error)} /> : null}
      onConfirm={() => {
        if (plan) remove.mutate(plan.id, { onSuccess: () => onDone(plan) })
      }}
    />
  )
}
